import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { Check, Copy } from "lucide-react";
import { COLORS } from "../hooks/useAudioRecorder";
import { useProfileApi } from "../api/profile";
import { useApiClient } from "../api/client";
import { generateInvoicePdf, safeInvoiceFilename } from "../utils/generateInvoicePdf";
import {
  triggerBlobDownload,
  shareOrDownloadPdf,
  canSharePdfFiles,
  isLikelyMobile,
} from "../utils/triggerDownload";
import { formatInvoiceDate } from "../utils/dateFormat";
import PageShell from "../components/PageShell";
import AuthChip from "../components/AuthChip";
import BackLink from "../components/BackLink";
import BrandMark from "../components/BrandMark";
import AppFooter from "../components/AppFooter";

const fmtMoney = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return `$${Number(n).toLocaleString(undefined, {
    minimumFractionDigits: Number.isInteger(Number(n)) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

const fmtElapsed = (sec) => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  if (s < 60) return `${s} SECOND${s === 1 ? "" : "S"}`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (rem === 0) return `${m} MINUTE${m === 1 ? "" : "S"}`;
  return `${m}M ${rem}S`;
};

export default function Success() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  const api = useApiClient();
  const { fetchProfile } = useProfileApi();
  // Initial data from navigation state (fast path), then refetch/validate.
  const [meta, setMeta] = useState(() => location.state || null);
  const [invoice, setInvoice] = useState(null);
  const [profile, setProfile] = useState(() => location.state?.profile || null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [savePromptDismissed, setSavePromptDismissed] = useState(false);

  useEffect(() => {
    let mounted = true;
    api
      .get(`/sessions/${sessionId}/finalized`)
      .then((r) => {
        if (!mounted) return;
        setMeta({
          invoice_id: r.data.invoice_id,
          invoice_number: r.data.invoice_number,
          subtotal: r.data.subtotal,
          discount_total: r.data.discount_total,
          taxable_amount: r.data.taxable_amount,
          tax_amount: r.data.tax_amount,
          tax_label: r.data.tax_label,
          total_due: r.data.total_due,
          balance_due: r.data.balance_due,
          customer_name: r.data.customer_name,
          elapsed_seconds: r.data.elapsed_seconds,
          filename: r.data.filename,
          created_at: r.data.created_at,
          service_date: r.data.service_date,
          due_date: r.data.due_date,
        });
        setInvoice(r.data.invoice);
        if (r.data.profile) setProfile(r.data.profile);
      })
      .catch((e) => {
        if (mounted)
          setError(
            e?.response?.status === 404
              ? "This invoice has expired or was never finalized."
              : e?.message || String(e),
          );
      });
    return () => {
      mounted = false;
    };
  }, [api, sessionId]);

  // Ensure we have profile data for the SMS + preview header even if the
  // session snapshot didn't carry it (e.g. old finalization, or anonymous
  // flow where the BE only echoed back override fields).
  useEffect(() => {
    if (!authLoaded) return;
    let mounted = true;
    if (profile) return;
    fetchProfile()
      .then((p) => {
        if (mounted && p) setProfile(p);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [authLoaded, profile, fetchProfile]);

  const [downloading, setDownloading] = useState(false);
  // Pre-generated PDF state — built once invoice+meta load, then the share
  // button consumes it synchronously inside the tap gesture so iOS Safari's
  // Web Share API doesn't reject the call for losing user activation.
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfFilename, setPdfFilename] = useState(null);
  // Delayed indicator — only shows "Generating…" if pre-gen takes >200ms,
  // so a warm cache (sub-200ms) doesn't strobe the button label.
  const [showGenerating, setShowGenerating] = useState(false);
  const [canShare, setCanShare] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setCanShare(canSharePdfFiles());
    setIsMobile(isLikelyMobile());
  }, []);

  useEffect(() => {
    if (!invoice || !meta) return undefined;
    let cancelled = false;
    const generatingTimer = setTimeout(() => {
      if (!cancelled) setShowGenerating(true);
    }, 200);
    generateInvoicePdf({
      invoice,
      profile,
      meta: {
        invoice_number: meta?.invoice_number,
        subtotal: meta?.subtotal,
        discount_total: meta?.discount_total,
        taxable_amount: meta?.taxable_amount,
        tax_amount: meta?.tax_amount,
        tax_label: meta?.tax_label,
        total_due: meta?.total_due,
        balance_due: meta?.balance_due,
        client_name: meta?.customer_name,
        created_at: meta?.created_at,
        service_date: meta?.service_date,
        due_date: meta?.due_date,
      },
    })
      .then((blob) => {
        if (cancelled) return;
        clearTimeout(generatingTimer);
        setPdfBlob(blob);
        setPdfFilename(
          meta?.filename ||
            safeInvoiceFilename(meta?.invoice_number, meta?.customer_name),
        );
        setShowGenerating(false);
      })
      .catch((err) => {
        if (cancelled) return;
        clearTimeout(generatingTimer);
        // eslint-disable-next-line no-console
        console.warn("[Ledger] PDF pre-generation failed; tap will fall back to on-demand render.", err);
        setShowGenerating(false);
      });
    return () => {
      cancelled = true;
      clearTimeout(generatingTimer);
    };
  }, [invoice, meta, profile]);

  // Build (or reuse the pre-generated) PDF blob + filename. The mount effect
  // pre-generates into pdfBlob; this is the on-demand fallback if a tap lands
  // before that resolves or pre-gen failed.
  const ensureBlob = async () => {
    if (pdfBlob && pdfFilename) return { blob: pdfBlob, filename: pdfFilename };
    const blob = await generateInvoicePdf({
      invoice,
      profile,
      meta: {
        invoice_number: meta?.invoice_number,
        subtotal: meta?.subtotal,
        discount_total: meta?.discount_total,
        taxable_amount: meta?.taxable_amount,
        tax_amount: meta?.tax_amount,
        tax_label: meta?.tax_label,
        total_due: meta?.total_due,
        balance_due: meta?.balance_due,
        client_name: meta?.customer_name,
        created_at: meta?.created_at,
        service_date: meta?.service_date,
        due_date: meta?.due_date,
      },
    });
    const filename =
      meta?.filename ||
      safeInvoiceFilename(meta?.invoice_number, meta?.customer_name);
    return { blob, filename };
  };

  // Native share sheet — primary on phones ("Send"), secondary on desktop
  // ("Share"). Fast path is fire-and-forget: no await between the tap and
  // navigator.share() or iOS Safari voids the user-activation token.
  const handleShare = async () => {
    if (pdfBlob && pdfFilename) {
      shareOrDownloadPdf(pdfBlob, pdfFilename);
      return;
    }
    if (!invoice || downloading) return;
    setDownloading(true);
    try {
      const { blob, filename } = await ensureBlob();
      await shareOrDownloadPdf(blob, filename);
    } catch (e) {
      setError(`PDF generation failed: ${e?.message || e}`);
    } finally {
      setDownloading(false);
    }
  };

  // Direct download — never opens the share sheet. Primary on laptop/desktop.
  const handleDownloadOnly = async () => {
    if (pdfBlob && pdfFilename) {
      triggerBlobDownload(pdfBlob, pdfFilename);
      return;
    }
    if (!invoice || downloading) return;
    setDownloading(true);
    try {
      const { blob, filename } = await ensureBlob();
      triggerBlobDownload(blob, filename);
    } catch (e) {
      setError(`PDF generation failed: ${e?.message || e}`);
    } finally {
      setDownloading(false);
    }
  };

  // Phone (touch-primary + can share files) → single "Send" button. Laptop →
  // "Download PDF" primary + optional "Share" secondary when the browser
  // supports Web Share with files (Windows Chrome/Edge).
  const mobileSend = isMobile && canShare;
  const showShareButton = !mobileSend && canShare;
  const primaryLabel = downloading || showGenerating
    ? "Generating…"
    : mobileSend
      ? "Send invoice"
      : "Download PDF";

  const smsBody = useMemo(() => {
    const client =
      (invoice?.customer?.name || meta?.customer_name || "").trim() || "there";
    const business =
      (profile?.business_name || "").trim() || "your contractor";
    const total = fmtMoney(meta?.total_due || 0);
    return `Hi ${client}, your invoice from ${business} is attached. Total: ${total}. Thank you!`;
  }, [invoice, meta, profile]);

  const onCopy = async () => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(smsBody);
      } else {
        const ta = document.createElement("textarea");
        ta.value = smsBody;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (e) {
      setError(`Copy failed: ${e.message || e}`);
    }
  };

  const onNewInvoice = () => {
    // Bump job number already happens per-tab on Home; just go back.
    navigate("/");
  };

  const businessName = (profile?.business_name || "Your Business").trim();
  const today = new Date().toLocaleDateString(undefined, {
    month: "long", day: "numeric", year: "numeric",
  });
  // Show the real issued date (created_at) and the service date, mirroring the
  // PDF — not a literal "now". Service date only shows when it differs from
  // the issued date (matches generateInvoicePdf's showServiceDate).
  const issuedDate = _previewFmtDate(meta?.created_at) || today;
  const serviceDateStr =
    meta?.service_date && _previewFmtDate(meta.service_date) !== issuedDate
      ? _previewFmtDate(meta.service_date)
      : "";

  return (
    <PageShell testId="success-page" padding="22px 22px 28px">
      <GlobalKeyframes />

      <div style={{ marginBottom: 16 }}>
        <BackLink to="/" label="Home" testId="success-back-link" />
      </div>

      {/* HandInvoice header */}
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 30,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <BrandMark size="header" />
          <span
            style={{
              fontFamily: "'Instrument Serif', Georgia, serif",
              fontStyle: "italic",
              fontSize: 22,
              lineHeight: 1,
            }}
          >
            HandInvoice
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <AuthChip />
          <div
            data-testid="success-invoice-number"
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 11,
              letterSpacing: "0.18em",
              color: COLORS.fainter,
              textTransform: "uppercase",
            }}
          >
            INVOICE #{meta?.invoice_number || "…"}
          </div>
        </div>
      </header>

      {error ? (
        <div
          data-testid="success-error"
          role="alert"
          style={{
            padding: "14px 16px",
            borderRadius: 12,
            border: `1px solid ${COLORS.red}`,
            backgroundColor: "rgba(229,75,60,0.08)",
            color: "#ffb3ab",
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 12,
            lineHeight: 1.5,
          }}
        >
          {error}
        </div>
      ) : !meta ? (
        <div
          data-testid="success-loading"
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 12,
            color: COLORS.faint,
            textAlign: "center",
            paddingTop: 40,
          }}
        >
          loading…
        </div>
      ) : (
        <>
          {/* Hero */}
          <section
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 14,
              marginBottom: 28,
            }}
          >
            <div
              data-testid="success-check-icon"
              style={{
                width: 64, height: 64, borderRadius: 999,
                backgroundColor: "rgba(74,222,128,0.12)",
                border: `1px solid ${COLORS.ok}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                animation: "success-pop 420ms cubic-bezier(.34,1.56,.64,1)",
              }}
            >
              <Check size={30} color={COLORS.ok} strokeWidth={2.5} />
            </div>
            <h1
              data-testid="success-title"
              style={{
                fontFamily: "'Instrument Serif', Georgia, serif",
                fontSize: 26,
                margin: 0,
                lineHeight: 1.1,
                textAlign: "center",
              }}
            >
              Invoice ready<span style={{ color: COLORS.yellow }}>.</span>
            </h1>
            <div
              data-testid="success-elapsed"
              style={{
                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                fontSize: 11,
                letterSpacing: "0.18em",
                color: COLORS.faint,
                textTransform: "uppercase",
              }}
            >
              // {fmtElapsed(meta.elapsed_seconds)} · GOOD WORK
            </div>
          </section>

          {/* PDF preview card — looks like physical paper */}
          <PreviewCard
            businessName={businessName}
            profile={profile}
            invoiceNumber={meta.invoice_number}
            date={issuedDate}
            serviceDate={serviceDateStr}
            dueDate={meta.due_date}
            invoice={invoice}
            subtotal={meta.subtotal}
            discountTotal={meta.discount_total}
            taxAmount={meta.tax_amount}
            taxLabel={meta.tax_label}
            totalDue={meta.total_due}
            balanceDue={meta.balance_due}
          />

          {/* SMS template card */}
          <div
            data-testid="success-sms-card"
            style={{
              marginTop: 22,
              backgroundColor: COLORS.panel,
              border: `1px solid ${COLORS.rule}`,
              borderRadius: 14,
              padding: "14px 14px 16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <span
                style={{
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 10,
                  letterSpacing: "0.2em",
                  color: COLORS.faint,
                  textTransform: "uppercase",
                }}
              >
                Ready-to-send SMS
              </span>
              <button
                data-testid="success-sms-copy-btn"
                onClick={onCopy}
                style={{
                  background: "transparent",
                  border: "none",
                  color: copied ? COLORS.ok : COLORS.yellow,
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 11,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  cursor: "pointer",
                  padding: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {copied ? (
                  <>
                    <Check size={12} /> COPIED
                  </>
                ) : (
                  <>
                    <Copy size={12} /> COPY
                  </>
                )}
              </button>
            </div>
            <div
              data-testid="success-sms-body"
              style={{
                backgroundColor: "#1f1c17",
                color: "#e6e2d4",
                padding: "12px 14px",
                borderRadius: "14px 14px 14px 4px",
                fontFamily: "'Inter', sans-serif",
                fontSize: 13.5,
                lineHeight: 1.5,
                maxWidth: "100%",
              }}
            >
              {smsBody}
            </div>
          </div>

          {/* Save-to-history prompt — only when user is anonymous */}
          {authLoaded && !isSignedIn && !savePromptDismissed && (
            <div
              data-testid="success-save-prompt"
              style={{
                marginTop: 22,
                padding: "14px 16px",
                borderRadius: 14,
                border: `1px solid ${COLORS.rule || "#3a362e"}`,
                backgroundColor: "#1a1917",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <div
                style={{
                  fontFamily: "'Instrument Serif', Georgia, serif",
                  fontStyle: "italic",
                  fontSize: 18,
                  color: "#f4f1ea",
                  lineHeight: 1.3,
                }}
              >
                Sign in before next time to save invoices
              </div>
              <div
                style={{
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 11,
                  color: "#8a8472",
                  letterSpacing: "0.04em",
                  lineHeight: 1.6,
                }}
              >
                // This invoice was generated anonymously and isn't saved.
                Sign in before generating to save future invoices to your
                account.
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                <button
                  data-testid="success-save-prompt-signin"
                  onClick={() => {
                    const target = `/success/${sessionId}`;
                    navigate(`/sign-in?redirect_url=${encodeURIComponent(target)}`);
                  }}
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    borderRadius: 999,
                    backgroundColor: COLORS.yellow,
                    color: "#0f0e0c",
                    border: "none",
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 800,
                    fontSize: 12,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    cursor: "pointer",
                  }}
                >
                  Sign in
                </button>
                <button
                  data-testid="success-save-prompt-skip"
                  onClick={() => setSavePromptDismissed(true)}
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    borderRadius: 999,
                    border: `1px solid ${COLORS.rule || "#3a362e"}`,
                    backgroundColor: "transparent",
                    color: "#a8a29a",
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 600,
                    fontSize: 12,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    cursor: "pointer",
                  }}
                >
                  Skip
                </button>
              </div>
            </div>
          )}

          {/* Action buttons — primary download, then prominent "make
              another" secondary so users have a clear restart path. */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              marginTop: 22,
            }}
          >
            <button
              data-testid="success-download-btn"
              onClick={mobileSend ? handleShare : handleDownloadOnly}
              disabled={downloading || !invoice}
              style={{
                width: "100%",
                padding: "15px 18px",
                borderRadius: 999,
                backgroundColor: COLORS.yellow,
                color: "#0f0e0c",
                border: "none",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 800,
                fontSize: 13,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                textAlign: "center",
                cursor: downloading || !invoice ? "default" : "pointer",
                opacity: !invoice ? 0.5 : 1,
                boxShadow: "0 24px 60px -18px rgba(232,220,45,0.5)",
              }}
            >
              {primaryLabel}
            </button>
            {showShareButton && (
              <button
                data-testid="success-share-btn"
                onClick={handleShare}
                disabled={downloading || !invoice}
                style={{
                  width: "100%",
                  padding: "14px 18px",
                  borderRadius: 999,
                  border: `1px solid ${COLORS.rule}`,
                  backgroundColor: "transparent",
                  color: COLORS.text,
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: 600,
                  fontSize: 13,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  cursor: downloading || !invoice ? "default" : "pointer",
                  opacity: !invoice ? 0.5 : 1,
                }}
              >
                Share
              </button>
            )}
            <button
              data-testid="success-new-invoice-btn"
              onClick={onNewInvoice}
              style={{
                width: "100%",
                padding: "14px 18px",
                borderRadius: 999,
                border: `1px solid ${COLORS.rule}`,
                backgroundColor: "transparent",
                color: COLORS.text,
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                fontSize: 13,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                cursor: "pointer",
                transition: "border-color 160ms ease, color 160ms ease",
              }}
            >
              Make another invoice
            </button>
          </div>

        </>
      )}
      <AppFooter />
    </PageShell>
  );
}

/* -------------------------------------------------------------------------- */
/*  PreviewCard — paper-like render of the invoice                            */
/* -------------------------------------------------------------------------- */


// Parse an ISO "YYYY-MM-DD" as a LOCAL date (avoid the UTC-midnight shift that
// new Date("2026-06-05") introduces) and format it short.
// Date-only-aware formatter (utils/dateFormat.js) — shared, tested, matches
// the PDF. Pure "YYYY-MM-DD" → local midnight; timestamps → instant.
const _previewFmtDate = (iso) => formatInvoiceDate(iso, { month: "short" });

// "Due date: <date>" — the backend already applied the terms to compute the
// due date, so we just show the date (no "Net N" suffix). Matches the PDF.
const _previewDueBy = (dueDate) => {
  const dueStr = _previewFmtDate(dueDate);
  return dueStr ? `Due date: ${dueStr}` : "";
};

const PreviewCard = ({
  businessName,
  profile,
  invoiceNumber,
  date,
  serviceDate,
  dueDate,
  invoice,
  subtotal,
  discountTotal,
  taxAmount,
  taxLabel,
  totalDue,
  balanceDue,
}) => {
  const inv = invoice || {};
  const customer = inv.customer || {};
  const job = inv.job || {};
  const lineItems = (
    Array.isArray(inv.line_items)
      ? [...inv.line_items].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      : []
  ).filter((it) => Number(it.amount) >= 0); // discounts show in totals, not here

  const subtotalN = Number(subtotal ?? 0);
  const hasDiscount = Number.isFinite(Number(discountTotal)) && Number(discountTotal) !== 0;
  // subtotal is stored net of the discount; show the gross items sum and let
  // the DISCOUNT row bring it down (gross + discount = net).
  const grossSubtotalN = +(subtotalN - Number(discountTotal || 0)).toFixed(2);
  const hasTax = Number(taxAmount) > 0;
  const deposit = inv.deposit;
  const hasDeposit = deposit && Number(deposit.amount) > 0;
  const depositAmount = hasDeposit ? Number(deposit.amount) : 0;
  const finalNumber = Number(
    balanceDue ?? totalDue ?? subtotalN + (Number(taxAmount) || 0),
  );

  // Build the conditional contractor-contact block only from present values.
  const contactLineParts = [];
  if (profile?.phone) contactLineParts.push(profile.phone);
  if (profile?.email) contactLineParts.push(profile.email);
  const contractorName = profile?.contractor_name || "";
  const address = profile?.address || "";
  const licenseNumber = profile?.license_number || "";
  const tagline = profile?.tagline || "";
  // Per-invoice override wins over the profile default, mirroring the PDF
  // and the Review NotesBlock fallback chain. Treat explicit "" as "user
  // suppressed notes on this invoice" — fall back to "" not to the profile.
  const footerNotes =
    inv.customer_visible_notes !== null && inv.customer_visible_notes !== undefined
      ? String(inv.customer_visible_notes)
      : profile?.invoice_footer_notes || "";

  // Logo (view-only on screen; the PDF draws the same bytes). Raw base64 has
  // no "data:" prefix in the snapshot, so we rebuild the URL here.
  const logo = profile?.logo;
  const logoSrc =
    logo && logo.data
      ? `data:${logo.mime_type || "image/png"};base64,${logo.data}`
      : null;
  const dueBy = _previewDueBy(dueDate);
  const paymentMethods = (profile?.payment_methods || "").toString().trim();

  return (
    <div
      data-testid="success-preview-card"
      style={{
        backgroundColor: "#f4f1ea",
        color: "#1a1917",
        borderRadius: 10,
        padding: "22px 22px 20px",
        fontFamily: "'Helvetica', 'Arial', sans-serif",
        fontSize: 12.5,
        lineHeight: 1.45,
        boxShadow:
          "0 26px 60px -24px rgba(0,0,0,0.7), 0 2px 4px rgba(0,0,0,0.3)",
        transform: "rotate(-0.4deg)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        {logoSrc && (
          <img
            src={logoSrc}
            alt="Business logo"
            data-testid="success-preview-logo"
            style={{
              width: 54,
              height: 54,
              objectFit: "contain",
              flex: "0 0 auto",
            }}
          />
        )}
        <div style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
          <div
            data-testid="success-preview-business-name"
            style={{
              fontFamily: "'Instrument Serif', Georgia, serif",
              fontStyle: "italic",
              fontSize: 20,
              lineHeight: 1,
            }}
          >
            {businessName}
          </div>
          {tagline && (
            <div
              data-testid="success-preview-tagline"
              style={{
                fontSize: 11,
                color: "#555",
                fontStyle: "italic",
                marginTop: 3,
              }}
            >
              {tagline}
            </div>
          )}
          {(contractorName || contactLineParts.length > 0 || address || licenseNumber) && (
            <div style={{ marginTop: 6, fontSize: 11, color: "#444" }}>
              {contractorName && (
                <div style={{ fontWeight: 600, color: "#1a1917" }}>{contractorName}</div>
              )}
              {contactLineParts.length > 0 && (
                <div>{contactLineParts.join("  ·  ")}</div>
              )}
              {address && <div>{address}</div>}
              {licenseNumber && <div>Lic. #{licenseNumber}</div>}
            </div>
          )}
        </div>
        <div style={{ textAlign: "right", fontFamily: "'JetBrains Mono', ui-monospace, monospace", flex: "0 0 auto" }}>
          <div style={{ fontSize: 9, letterSpacing: "0.2em", color: "#888", textTransform: "uppercase" }}>
            Invoice
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, marginTop: 2 }}>#{invoiceNumber}</div>
          <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>{date}</div>
          {serviceDate && (
            <div
              data-testid="success-preview-service-date"
              style={{ fontSize: 10, color: "#666", marginTop: 2 }}
            >
              Service {serviceDate}
            </div>
          )}
          {dueBy && (
            <div
              data-testid="success-preview-due-by"
              style={{ fontSize: 10, color: "#666", marginTop: 2 }}
            >
              {dueBy}
            </div>
          )}
        </div>
      </div>
      <div style={{ borderTop: "2px solid #1a1917", margin: "14px 0 18px" }} />

      {/* BILL TO / JOB SITE bands */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, overflowWrap: "anywhere" }}>
        <div>
          <div
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 9,
              letterSpacing: "0.2em",
              color: "#888",
              textTransform: "uppercase",
              marginBottom: 4,
            }}
          >
            Bill to
          </div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{customer.name || "—"}</div>
          {customer.company && (
            <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>{customer.company}</div>
          )}
          {customer.address && (
            <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>{customer.address}</div>
          )}
          {customer.phone && (
            <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>{customer.phone}</div>
          )}
          {customer.email && (
            <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>{customer.email}</div>
          )}
        </div>
        <div>
          <div
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 9,
              letterSpacing: "0.2em",
              color: "#888",
              textTransform: "uppercase",
              marginBottom: 4,
            }}
          >
            Job site
          </div>
          {job.site_address ? (
            <div style={{ fontSize: 12, color: "#1a1917" }}>{job.site_address}</div>
          ) : (
            <div style={{ fontSize: 12, color: "#888", fontStyle: "italic" }}>
              Same as billing
            </div>
          )}
          {job.description && (
            <div
              style={{
                fontSize: 11,
                color: "#555",
                marginTop: 4,
                fontStyle: "italic",
              }}
            >
              {job.description}
            </div>
          )}
        </div>
      </div>

      {/* Line items table */}
      <table style={{ width: "100%", marginTop: 18, borderCollapse: "collapse" }}>
        <tbody>
          {lineItems.length === 0 && (
            <tr>
              <td colSpan={2} style={{ padding: "12px 0", fontSize: 12, color: "#888", fontStyle: "italic" }}>
                No line items.
              </td>
            </tr>
          )}
          {(() => {
            const out = [];
            lineItems.forEach((it, i) => {
              const isNegative = Number(it.amount) < 0;
              const detail = [
                it.quantity != null && it.unit ? `${it.quantity} ${it.unit}` : (it.quantity != null ? String(it.quantity) : ""),
                it.unit_price != null && it.unit_price !== "" ? `${fmtMoney(it.unit_price)}/${it.unit || "ea"}` : "",
              ]
                .filter(Boolean)
                .join(" · ");
              const hasNotes = !!it.notes;
              out.push(
                <tr key={it.id || i}>
                  <td
                    style={{
                      padding: "8px 0 4px",
                      borderBottom: hasNotes ? "none" : "1px solid #e2dfd5",
                    }}
                  >
                    <div style={{ fontSize: 12 }}>{it.description || "—"}</div>
                    {detail && (
                      <div style={{ fontSize: 10, color: "#777", marginTop: 2 }}>{detail}</div>
                    )}
                  </td>
                  <td
                    style={{
                      padding: "8px 0 4px",
                      textAlign: "right",
                      borderBottom: hasNotes ? "none" : "1px solid #e2dfd5",
                      verticalAlign: "top",
                      whiteSpace: "nowrap",
                      color: isNegative ? "#a04030" : "#1a1917",
                    }}
                  >
                    {fmtMoney(it.amount)}
                  </td>
                </tr>,
              );
              if (hasNotes) {
                out.push(
                  <tr key={`n${i}`}>
                    <td
                      colSpan={2}
                      style={{
                        padding: "0 0 8px",
                        borderBottom: "1px solid #e2dfd5",
                        fontSize: 10.5,
                        color: "#777",
                        fontStyle: "italic",
                      }}
                    >
                      {it.notes}
                    </td>
                  </tr>,
                );
              }
            });
            return out;
          })()}
        </tbody>
      </table>

      {/* Totals area */}
      <table style={{ width: "100%", marginTop: 10, borderCollapse: "collapse" }}>
        <tbody>
          <TotalsLine label="Subtotal" amount={fmtMoney(grossSubtotalN)} testid="success-preview-subtotal" />
          {hasDiscount && (
            <TotalsLine label="Discount" amount={fmtMoney(discountTotal)} tone="discount" />
          )}
          {hasTax && (
            <TotalsLine
              label={taxLabel || "Tax"}
              amount={fmtMoney(taxAmount)}
              testid="success-preview-tax-amount"
            />
          )}
          {hasDeposit && (
            <TotalsLine
              label={
                deposit?.method && String(deposit.method).trim()
                  ? `Deposit received (${String(deposit.method).trim()})`
                  : "Deposit received"
              }
              amount={fmtMoney(-depositAmount)}
              tone="discount"
            />
          )}
        </tbody>
      </table>

      <div
        style={{
          marginTop: 8,
          borderTop: "2px solid #1a1917",
          paddingTop: 12,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
        }}
      >
        <span
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 10,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
          }}
        >
          Balance Due
        </span>
        <span
          data-testid="success-preview-total"
          style={{ fontSize: 22, fontWeight: 700 }}
        >
          {fmtMoney(finalNumber)}
        </span>
      </div>

      {paymentMethods && (
        <div
          data-testid="success-preview-payment-methods"
          style={{
            marginTop: 16,
            paddingTop: 12,
            borderTop: "1px dashed #c8c4b7",
          }}
        >
          <div
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 9,
              letterSpacing: "0.2em",
              color: "#888",
              textTransform: "uppercase",
              marginBottom: 4,
            }}
          >
            Payment
          </div>
          <div style={{ fontSize: 11, color: "#555", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
            {paymentMethods}
          </div>
        </div>
      )}

      {footerNotes && (
        <div
          data-testid="success-preview-footer-notes"
          style={{
            marginTop: 16,
            paddingTop: 12,
            borderTop: "1px dashed #c8c4b7",
          }}
        >
          <div
            style={{
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontSize: 9,
              letterSpacing: "0.2em",
              color: "#888",
              textTransform: "uppercase",
              marginBottom: 4,
            }}
          >
            Notes
          </div>
          <div
            style={{
              fontSize: 11,
              color: "#555",
              fontStyle: "italic",
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
            }}
          >
            {footerNotes}
          </div>
        </div>
      )}
    </div>
  );
};

// Shared right-aligned label/amount row used by the preview's totals area.
function TotalsLine({ label, amount, tone, testid }) {
  const isDiscount = tone === "discount";
  const isMuted = tone === "muted";
  return (
    <tr data-testid={testid}>
      <td
        style={{
          padding: "4px 0",
          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
          fontSize: 10,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          color: isMuted ? "#888" : "#555",
        }}
      >
        {label}
      </td>
      <td
        style={{
          padding: "4px 0",
          textAlign: "right",
          fontSize: 12,
          whiteSpace: "nowrap",
          color: isDiscount ? "#a04030" : isMuted ? "#888" : "#1a1917",
        }}
      >
        {amount}
      </td>
    </tr>
  );
}

const GlobalKeyframes = () => (
  <style>{`
    @keyframes success-pop {
      0% { transform: scale(0.6); opacity: 0; }
      70% { transform: scale(1.08); opacity: 1; }
      100% { transform: scale(1); opacity: 1; }
    }
  `}</style>
);
